import { Box, Checkbox, FormControlLabel } from "@mui/material";
import { GridRowSelectionModel } from "@mui/x-data-grid";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import ElevatedPaper from "../../../../components/ElevatedPaper";
import Flex from "../../../../components/Flex";
import PageLayout from "../../../../components/PageLayout";
import SearchInput from "../../../../components/SearchInput";
import CustomTabs from "../../../../components/app/CustomTabs";
import StyledButtonV2 from "../../../../components/app/StyledButtonV2";
import { useRoleController } from "../../../../components/useRoleController";
import { useDebounce } from "../../../../hooks/use-debounce";
import useDisclosure from "../../../../hooks/use-disclosure";
import { useDownloadReport } from "../../../../hooks/use-download-report";
import { useReduceDisclosure } from "../../../../hooks/use-reduce-disclosure";
import { useSnackbar } from "../../../../hooks/useSnackbar";
import { useConfiguredClearinghouses } from "../../../../queries/billings/claim-integration/use-configured-clearinghouses";
import { useAddElectronicClaim } from "../../../../queries/billings/claims/use-add-electronic-claim";
import { useBatchPrintInvoice } from "../../../../queries/use-batch-print-invoice";
import { ClaimRequestVendorTypeEnum } from "../../../../services/api";
import {
  getErrorSnackbar,
  getSuccessSnackbar,
  roleFilter,
} from "../../../../utils/utils";
import BillingFilter, { FilterData } from "./BillingsFilter";
import DownloadZipModal from "./DownloadZipModal";
import SuperBillFilter, { SuperBillFilterData } from "./SuperBillFilter";
import { DEFAULT_BILLED_STATUS, tabsData } from "./billing-constant";
import ClaimsFilters from "./claims/ClaimsFilters";
import CreateClaimDrawer from "./claims/CreateClaimDrawer";
import CreateClaimPopUp from "./claims/CreateClaimPopUp";
// import SubmitBatchClaimsPopUp from "./claims/SubmitBatchClaimsPopUp";
// import BatchClaimModal from "./claims/batch-claims/BatchClaimModal";
import Text from "../../../../components/Text";
import CreateInvoiceTypeMenu from "./CreateInvoiceTypeMenu";
import CopyClaimModal from "./claims/copy-claim/CopyClaimModal";
import ScrubClaimsModal from "./claims/scrub-claims/ScrubClaimsModal";
import NavSelectClaimTypeModal from "./claims/updateClaims/NavSelectClaimTypeModal";
import EraEobFilters, { EraEobFilterData } from "./era-eob/EraEobFilters";
import UploadEobModal from "./era-eob/UploadEobModal";
import UploadEraEobModal from "./era-eob/UploadEraEobModal";

export const DEFAULT_SUPER_BILL_FILTERS = {
  archive: false,
  visit_Id: undefined,
  provider_Name: undefined,
  bill_Status: undefined,
  date_range: undefined,
  startDate: undefined,
  endDate: undefined,
};
export const DEFAULT_CLAIMS_FILTERS = {
  claim_statuses: undefined,
  date_range: undefined,
  startDate: undefined,
  endDate: undefined,
  patients: undefined,
  providers: undefined,
  // scrubStatus: undefined,
  claimId: undefined,
  claim_type: undefined,
};

const DEFAULT_ERA_EOB_FILTERS_STATE: EraEobFilterData = {
  date_range: undefined,
  startDate: undefined,
  endDate: undefined,
  providers: undefined,
  claim_type: undefined,
  view_claim: undefined,
};

export type ClaimFilterData = {
  claim_statuses: undefined;
  date_range: undefined;
  startDate: string | undefined;
  endDate: string | undefined;
  patients: undefined;
  providers: undefined;
  // scrubStatus: undefined;
  claimId: string | undefined;
  claim_type: undefined;
};

// ECRH-5369: Batch Submit only on Ready to Submit; Batch Scrub on Draft + Ready to Submit.
const BATCH_SUBMIT_STATUSES = new Set(["READY_FOR_SUBMIT"]);
const BATCH_SCRUB_STATUSES = new Set(["DRAFT", "READY_FOR_SUBMIT"]);

const BillingLayout = () => {
  const navigate = useNavigate();
  const [addClaimsDisclosure, copyClaimDisclosure] = useReduceDisclosure(2);

  const showSnackbar = useSnackbar();
  const queryClient = useQueryClient();

  const { hasNone: hasNoneEncounterBilling } = useRoleController(
    "Encounter for billing"
  );

  const { hasUpdate: hasUpdateSuperbill, hasNone: hasNoneSuperbill } =
    useRoleController("Generate Superbill");
  const { hasUpdate: hasUpdateClaims, hasNone: hasNoneClaims } =
    useRoleController("Generate Claims");
  const { hasUpdate: hasUpdateBatchClaimSubmission } = useRoleController(
    "Batch claim submission"
  );

  // Derive active tab from URL so it stays in sync with what's actually rendered.
  const location = useLocation();
  const activeTab = (() => {
    const segments = location.pathname.split("/").filter(Boolean);
    const knownTabs = new Set(["", "claims", "invoice", "era_eob"]);
    const match = segments.find((seg) => knownTabs.has(seg));
    return match ?? "";
  })();
  // const [pagination, setPagination] = useState<DGT_Pagination>(DEF_PAGINATION);

  const DEFAULT_ENCOUNTER_FILTERS: FilterData = {
    provider_Name: undefined,
    appointment_Type: undefined,
    billed_Status: DEFAULT_BILLED_STATUS,
    date_range: undefined,
    startDate: undefined,
    endDate: undefined,
  };

  const [, setSearchStringKey, instantSearchStringKey] =
    useDebounce<string>("");
  // const [
  //   superBillSearchStringKey,
  //   setSuperBillSearchStringKey,
  //   instantSuperBillSearchStringKey,
  // ] = useDebounce<string>("");
  // const [
  //   eraEobSearchStringKey,
  //   setEraEobSearchStringKey,
  //   instantEraEobSearchStringKey,
  // ] = useDebounce<string>("");

  const [selectedFilter, setSelectedFilter] = useState<{
    "": FilterData;
    invoice: SuperBillFilterData;
    claims: ClaimFilterData;
    era_eob: EraEobFilterData;
  }>({
    "": DEFAULT_ENCOUNTER_FILTERS,
    invoice: DEFAULT_SUPER_BILL_FILTERS,
    claims: DEFAULT_CLAIMS_FILTERS,
    era_eob: DEFAULT_ERA_EOB_FILTERS_STATE,
  });

  const setFilterFor =
    <K extends keyof typeof selectedFilter>(key: K) =>
    (val: (typeof selectedFilter)[K]) =>
      setSelectedFilter((prev) => ({ ...prev, [key]: val }));
  // const [gridSorting, setGridSorting] = useState<GridSortItem[]>([]);

  // const {
  //   data: appointmentsList,
  //   isLoading,
  //   isFetching,
  // } = useAppointmentsV2({
  //   pagination: pagination,
  //   gridSorting: gridSorting,
  //   providerGroupId: getXtenantId(),
  //   appointmentStatus: ["COMPLETED"],
  //   // archieve: false,
  //   providerUuid:
  //     (encounterBillingFilter.provider_Name as any)?.value ?? undefined,
  //   appointmentType: encounterBillingFilter.appointment_Type?.value
  //     ? [encounterBillingFilter.appointment_Type?.value]
  //     : undefined,
  //   searchKey: searchStringKey,
  //   superBillGenerated: false,
  //   dateRangeType: encounterBillingFilter?.date_range ?? undefined,
  //   startDate:
  //     encounterBillingFilter?.date_range === "CUSTOM_RANGE"
  //       ? dayjs(encounterBillingFilter?.startDate).toISOString()
  //       : undefined,
  //   endDate:
  //     encounterBillingFilter?.date_range === "CUSTOM_RANGE"
  //       ? dayjs(encounterBillingFilter?.endDate).toISOString()
  //       : undefined,
  // });

  const handleTabChange = (value: string) => {
    navigate(`/app/provider/billing/${value}`);
    setSearchStringKey("");
  };

  // const COLUMNS = applyDefaultColumnSettings<AppointmentSchedulingType>(
  //   [
  //     {
  //       field: "uuid",
  //       flex: 0.7,
  //       minWidth: 120,
  //       headerName: "Visit ID",
  //       sortable: false,
  //       renderCell: ({ row }) => {
  //         return (
  //           <Text isTabletext>{row?.uuid?.split("-")?.[0]?.toUpperCase()}</Text>
  //         );
  //       },
  //     },
  //     {
  //       field: "appointmentDate",
  //       flex: 1,
  //       minWidth: 170,
  //       headerName: "Visit Date & Time",
  //       sortable: false,
  //       renderCell: ({ row }) => formatDateTime(row?.startTime) || "-",
  //     },
  //     // {
  //     //   field: " ",
  //     //   flex: 0.6,
  //     //   minWidth: 160,
  //     //   headerName: "Appointment Mode",
  //     //   sortable: false,
  //     //   renderCell: ({ row }) =>
  //     //     row?.mode ? NEW_APPOINTMENT_VISIT_TYPE_MAPPER[row?.mode as any] : "-",
  //     // },
  //     // {
  //     //   field: "patientId",
  //     //   flex: 0.6,
  //     //   minWidth: 150,
  //     //   headerName: "Patient ID",
  //     //   sortable: false,
  //     //   renderCell: ({ row }) => {
  //     //     return <Text>{row?.patientId?.split("-")?.[0]?.toUpperCase()}</Text>;
  //     //   },
  //     // },
  //     {
  //       field: "patientName",
  //       flex: 0.6,
  //       minWidth: 210,
  //       disableColumnMenu: true,
  //       headerName: "Patient Name",
  //       renderCell: ({ row }) => (
  //         <PatientName
  //           name={row?.patientName}
  //           gender={row?.patientGender}
  //           patientId={row?.patientId!}
  //           birthDate={row?.patientDob}
  //           contactNo={row?.patientPhone}
  //         />
  //       ),
  //     },
  //     {
  //       field: "providerName",
  //       flex: 1,
  //       minWidth: 150,
  //       headerName: "Provider",
  //       sortable: false,
  //       renderCell: ({ row }) => {
  //         return (
  //           <Box>
  //             <Text isTabletext sx={{ lineHeight: 1.2 }}>
  //               {row?.providerName ?? "-"}
  //             </Text>
  //             {/* <Text isTabletext c={"gray"} sx={{ lineHeight: 1.2 }}>
  //               {row?.providerRole ?? ""}
  //             </Text> */}
  //           </Box>
  //         );
  //       },
  //     },
  //     {
  //       field: "type",
  //       flex: 1,
  //       minWidth: 150,
  //       headerName: "Appointment Type",
  //       sortable: false,
  //       renderCell: ({ row }) =>
  //         row?.type ? capitalize(row?.type.replace(/_/g, " ")) : "-",
  //     },
  //     {
  //       field: "chiefComplaint",
  //       flex: 1.2,
  //       minWidth: 150,
  //       headerName: "Chief Complaint",
  //       sortable: false,
  //       renderCell: ({ value }) => <RenderWithTooltip value={value} />,
  //     },
  //     {
  //       field: "locationName",
  //       flex: 0.8,
  //       minWidth: 150,
  //       headerName: "Place of Visit",
  //       sortable: false,
  //       renderCell: ({ value }) => value || "-",
  //     },
  //     // {
  //     //   field: "modified",
  //     //   flex: 0.8,
  //     //   minWidth: 150,
  //     //   headerName: "Last Updated",
  //     //   sortable: false,
  //     //   renderCell: ({ value }) => (
  //     //     <RenderWithTooltip value={formatDateTime(value)} />
  //     //   ),
  //     // },
  //     {
  //       field: "paymentMode",
  //       flex: 0.6,
  //       minWidth: 160,
  //       headerName: "Payment Mode",
  //       sortable: false,
  //       renderCell: ({ value }) => (value ? value : "-"),
  //     },
  //     {
  //       field: "Action",
  //       width: 90,
  //       hideSortIcons: true,
  //       renderCell: ({ row }) => {
  //         return (
  //           <ActionMenu
  //             options={roleFilter({
  //               list: [
  //                 { value: "view", label: "View Visit" },
  //                 { value: "createSuperBill", label: "Create Invoice" },
  //                 { value: "createClaim", label: "Create Claim" },
  //               ],
  //               compWith: {
  //                 view: hasView,
  //                 createSuperBill: hasUpdateSuperbill,
  //                 createClaim: hasUpdateClaims,
  //               },
  //               removeOtherFields: hasReadOnlyEncounterBilling,
  //             })}
  //             onActionClick={(v) => {
  //               if (v === "view") {
  //                 viewEncounterDisclosure.open(row.uuid);
  //               } else if (v === "createSuperBill") {
  //                 navigate(
  //                   row?.uuid
  //                     ? "invoice/" + row?.patientId + "/" + row?.uuid
  //                     : "1"
  //                 );
  //               } else if (v === "createClaim") {
  //                 addClaimsDisclosure.open(row);
  //               }
  //             }}
  //           />
  //         );
  //       },
  //     },
  //   ],
  //   ["patientName", "appointmentDate"]
  // // );

  const navSelectClaimTypeDisc = useDisclosure(false);
  const scrubClaimsDisclosureHost = useDisclosure(false);
  // const batchScrubClaimsDisclosureHost = useDisclosure(false);

  // ECRH-5015: paired with the hidden SubmitBatchClaimsPopUp above.
  // const handleBatchScrubClaims = (v: string) => {
  //   if (v === "electronic_claim") {
  //     batchScrubClaimsDisclosureHost.open();
  //   } else if (v === "paper_claim") {
  //     console.log("paper_claim");
  //   }
  // };
  const uploadEobDisclosure = useDisclosure();
  const uploadEraDisclosure = useDisclosure();

  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>([]);

  const [selectedClaimCard, setSelectedClaimCard] = useState<
    string | undefined
  >("ALL");

  const [paperClaimFilter, setPaperClaimFilter] = useState<boolean>(false);

  const claimStatusFilter = (selectedFilter.claims as ClaimFilterData)
    ?.claim_statuses as { value: string }[] | undefined;

  // ECRH-5369: derive the effective claim status(es) in view — from the status
  // filter if set, otherwise the selected status card ("ALL" = no restriction).
  const getEffectiveClaimStatuses = (): string[] => {
    if (claimStatusFilter?.length)
      return claimStatusFilter.map((s) => s?.value);
    if (selectedClaimCard && selectedClaimCard !== "ALL")
      return [selectedClaimCard];
    return [];
  };
  const effectiveClaimStatuses = getEffectiveClaimStatuses();

  const showBatchClaimSubmission =
    effectiveClaimStatuses.length > 0 &&
    effectiveClaimStatuses.every((s) => BATCH_SUBMIT_STATUSES.has(s));

  const showScrubClaims =
    effectiveClaimStatuses.length > 0 &&
    effectiveClaimStatuses.every((s) => BATCH_SCRUB_STATUSES.has(s));

  // Row checkboxes are only useful when one of the batch actions is available.
  const isBulkClaimActionAllowed = showBatchClaimSubmission || showScrubClaims;

  // The "Paper Claims" toggle only applies to the Ready for Submit view, which
  // shows electronic claims unless the toggle is on.
  const isReadyForSubmitView =
    effectiveClaimStatuses.length === 1 &&
    effectiveClaimStatuses[0] === "READY_FOR_SUBMIT";

  // ECRH-4811: Reset row selection on Invoice/SuperBill filter change.
  // ECRH-5015: Same for claim status filter / card switch.
  // The selection is shared by every Billing tab and the grid keeps rows
  // selected across pages, so it also has to be dropped whenever the set of
  // rows on screen changes: switching tab (invoice uuids must never reach the
  // claim batch), toggling Paper Claims, or applying any claim filter — not
  // just the status one. Otherwise a batch submit sends uuids that are no
  // longer valid Ready-for-Submit electronic claims and the whole batch fails.
  useEffect(() => {
    setRowSelectionModel([]);
  }, [
    activeTab,
    selectedFilter.invoice,
    selectedFilter.claims,
    claimStatusFilter,
    selectedClaimCard,
    paperClaimFilter,
  ]);

  useEffect(() => {
    if (claimStatusFilter) setSelectedClaimCard(undefined);
  }, [claimStatusFilter]);

  // Leaving Ready for Submit drops the paper-claims restriction.
  useEffect(() => {
    if (!isReadyForSubmitView) setPaperClaimFilter(false);
  }, [isReadyForSubmitView]);

  const { mutateAsync: addElectronicClaim, isPending: isBatchSubmitting } =
    useAddElectronicClaim();
  // When only one clearinghouse is configured, submit directly to it and skip
  // the vendor-selection screen. Until the configuration has loaded there is no
  // vendor to submit to, so the button has to stay disabled rather than fall
  // through to the "pick a vendor" screen with nothing in it.
  const { singleVendor, isLoading: isClearinghousesLoading } =
    useConfiguredClearinghouses();
  const [batchPrintModalOpen, setBatchPrintModalOpen] = useState(false);
  const { mutateAsync: batchPrintInvoice } = useBatchPrintInvoice();
  const { download: downloadBatchInvoice } = useDownloadReport({
    onSuccess: () => {
      setRowSelectionModel([]);
      setBatchPrintModalOpen(false);
    },
  });

  const handleBatchInvoicePrint = () => {
    setBatchPrintModalOpen(true);
    downloadBatchInvoice({
      apiCall: batchPrintInvoice(rowSelectionModel as string[]),
      fileName: "BatchInvoices.zip",
      contentType: "application/zip",
    });
  };

  const handleBatchClaimSubmission = (v: string) => {
    // Guard the payload before it leaves: an empty selection or a missing
    // vendor used to be sent as-is, and the backend rejection was swallowed by
    // the shared toast helper, so the click looked like it did nothing.
    const claimUuids = (rowSelectionModel as string[]).filter(Boolean);
    if (claimUuids.length === 0) {
      showSnackbar({
        message: "Please select at least one claim to submit",
        type: "error",
      });
      return;
    }
    if (!v) {
      showSnackbar({ message: "Please select a claim vendor", type: "error" });
      return;
    }
    // A second click while the first batch is still in flight resubmits the
    // same uuids; the backend rejects the duplicate and both attempts fail.
    if (isBatchSubmitting) return;

    addElectronicClaim({
      claim: { claimUuids, vendorType: v as ClaimRequestVendorTypeEnum },
    })
      .then((res) => {
        // The shared helper only raises a toast when the response carries a
        // message, so a message-less success was indistinguishable from a
        // no-op. Always confirm the submission.
        showSnackbar({
          type: "success",
          message:
            getSuccessSnackbar(res).message || "Claims submitted successfully",
        });
        navSelectClaimTypeDisc.close();
        queryClient.invalidateQueries({ queryKey: ["get-claims-list-v2"] });
        // The status cards are cached for 30s; without this the counts still
        // show the claims under Ready for Submit after they have moved on.
        queryClient.invalidateQueries({ queryKey: ["Get-claim-count"] });
        queryClient.invalidateQueries({ queryKey: ["appointment-list-v2"] });
        setRowSelectionModel([]);
      })
      .catch((err) => {
        // Keep the selection so the batch can be retried without re-picking.
        showSnackbar({
          type: "error",
          message:
            getErrorSnackbar(err).message ||
            "Batch claim submission failed. Please try again.",
        });
        navSelectClaimTypeDisc.close();
      });
  };
  // const selectFilter = useMemo(() => {
  //   const base = { rowSelectionModel, setRowSelectionModel };
  //   switch (activeTab) {
  //     case "claims":
  //       return {
  //         ...base,
  //         filter: selectedFilter.claims,
  //         setFilter: setFilterFor("claims"),
  //         navSelectClaimTypeDisc,
  //         copyClaimDisclosure,
  //         scrubClaimsDisclosureHost,
  //         batchScrubClaimsDisclosureHost,
  //       };
  //     case "super_bill":
  //       return {
  //         ...base,
  //         filter: selectedFilter.super_bill,
  //         setFilter: setFilterFor("super_bill"),
  //         searchKey: superBillSearchStringKey,
  //       };
  //     case "era_eob":
  //       return {
  //         ...base,
  //         filter: selectedFilter.era_eob,
  //         setFilter: setFilterFor("era_eob"),
  //         searchKey: eraEobSearchStringKey,
  //         uploadEobDisclosure,
  //         uploadEraDisclosure,
  //       };
  //     case "ready_for_billing":
  //       return {
  //         ...base,
  //         filter: selectedFilter.ready_for_billing,
  //         searchKey: searchStringKey,
  //         addClaimsDisclosure,
  //       };
  //     default:
  //       return base;
  //   }
  // }, [
  //   activeTab,
  //   rowSelectionModel,
  //   selectedFilter,
  //   superBillSearchStringKey,
  //   eraEobSearchStringKey,
  //   searchStringKey,
  // ]);
  const ActiveTabMapper = {
    "": (
      <Flex gap={1}>
        <SearchInput
          searchKey={instantSearchStringKey}
          onSearch={setSearchStringKey}
          placeholder="Search by Patient, Provider & Reason for Visit."
          name="patientName"
          sx={{ width: "20rem" }}
        />
        <BillingFilter
          onApply={(val) => {
            setFilterFor("")(val as FilterData);
          }}
          filters={selectedFilter[""]}
          setFilters={setFilterFor("")}
        />
      </Flex>
    ),
    invoice: (
      <Flex gap={1}>
        {/* <Box width={"15rem"}>
          <FormControlLabel
            control={
              <Checkbox
                checked={superBillFilter.archive}
                onChange={(e) =>
                  setSuperBillFilter({
                    ...superBillFilter,
                    archive: e.target.checked,
                  })
                }
              />
            }
            label="Show Archived Super Bills"
          />
        </Box> */}
        <Box width={"15rem"}>
          <SearchInput
            searchKey={instantSearchStringKey}
            onSearch={setSearchStringKey}
            placeholder="Search by Patient Name"
            name="patientName"
          />
        </Box>
        <SuperBillFilter
          filters={selectedFilter.invoice}
          setSuperBillFilters={setFilterFor("invoice")}
        />

        <StyledButtonV2
          disabled={rowSelectionModel.length === 0}
          style={{ width: "180px" }}
          variant="outlined"
          label={"Batch Invoice Print"}
          onClick={handleBatchInvoicePrint}
        />

        {hasUpdateSuperbill && <CreateInvoiceTypeMenu />}
      </Flex>
    ),
    claims: (
      <Flex gap={"10px"} alignItems={"center"}>
        {isReadyForSubmitView && (
          <FormControlLabel
            label={<Text>Paper Claims</Text>}
            control={
              <Checkbox
                checked={paperClaimFilter}
                onChange={(e) => {
                  setPaperClaimFilter(e.target.checked);
                }}
              />
            }
          />
        )}

        {hasUpdateBatchClaimSubmission && showBatchClaimSubmission && (
          <StyledButtonV2
            disabled={
              rowSelectionModel?.length === 0 ||
              isClearinghousesLoading ||
              isBatchSubmitting
            }
            label="Batch Claim Submission"
            variant="contained"
            onClick={() =>
              singleVendor
                ? handleBatchClaimSubmission(singleVendor)
                : navSelectClaimTypeDisc.open()
            }
          />
        )}
        <ClaimsFilters
          onApply={(val) => setFilterFor("claims")(val as ClaimFilterData)}
          filters={selectedFilter.claims}
          setFilters={setFilterFor("claims")}
        />

        {showScrubClaims && (
          <StyledButtonV2
            label="Scrub Claims"
            variant="outlined"
            disabled={rowSelectionModel?.length === 0}
            onClick={() => scrubClaimsDisclosureHost.open()}
          />
        )}
        {/* ECRH-5015: hide Submit Batch Claims (electronic/paper) popup. */}
        {/* <SubmitBatchClaimsPopUp
          onClick={(v) => {
            if (v === "electronic_claim") {
              handleBatchScrubClaims("electronic_claim");
            } else if (v === "paper_claim") {
              handleBatchScrubClaims("paper_claim");
            }
          }}
        /> */}

        {hasUpdateClaims && (
          <CreateClaimPopUp
            onClick={(v) => {
              if (v === "claim") addClaimsDisclosure.open();
              else copyClaimDisclosure.open();
            }}
          />
        )}
      </Flex>
    ),
    era_eob: (() => {
      const lastSegment = location.pathname.split("/").filter(Boolean).pop();
      const isEobWorklist = lastSegment === "eob";

      return (
        <Flex gap={1}>
          {!isEobWorklist && (
            <>
              <Box width={"18rem"}>
                <SearchInput
                  searchKey={instantSearchStringKey}
                  onSearch={setSearchStringKey}
                  placeholder="Search ERA ID"
                  name="search"
                />
              </Box>
              <EraEobFilters
                onApply={(val) =>
                  setFilterFor("era_eob")(val as EraEobFilterData)
                }
                filters={selectedFilter.era_eob}
                setFilters={setFilterFor("era_eob")}
              />
            </>
          )}
          {isEobWorklist ? (
            <StyledButtonV2
              label={"Add EOB"}
              onClick={() => uploadEobDisclosure.open()}
              shouldAddIcon
            />
          ) : (
            <StyledButtonV2
              label={"Upload ERA"}
              onClick={() => uploadEraDisclosure.open()}
              shouldAddIcon
            />
          )}
        </Flex>
      );
    })(),
  };

  return (
    <ElevatedPaper>
      <CreateClaimDrawer
        disClosureHost={addClaimsDisclosure}
        title="Create Claim"
      />

      <DownloadZipModal
        isModalOpen={batchPrintModalOpen}
        setIsModal={setBatchPrintModalOpen}
        message="Preparing ZIP download. You'll be notified when it's ready."
      />

      <PageLayout
        header={
          <Flex
            width={"100%"}
            justifyContent={"space-between"}
            alignItems={"center"}
          >
            <CustomTabs
              tabsData={roleFilter({
                list: tabsData,
                compWith: {
                  "": !hasNoneEncounterBilling,
                  invoice: !hasNoneSuperbill,
                  claims: !hasNoneClaims,
                },
              })}
              value={activeTab}
              onChange={handleTabChange}
            />
            <div>
              {ActiveTabMapper[activeTab as keyof typeof ActiveTabMapper]}
            </div>
          </Flex>
        }
      >
        {/* {activeTab === "super_bill" && (
          <SuperBill
            searchInputKey={superBillSearchStringKey}
            filter={superBillFilter}
            setSuperBillFilter={setSuperBillFilter}
            rowSelectionModel={rowSelectionModel}
            setRowSelectionModel={setRowSelectionModel}
          />
        )} */}
        {activeTab === "claims" && (
          <>
            <NavSelectClaimTypeModal
              disclosureHost={navSelectClaimTypeDisc}
              onNextClick={(v) => {
                if (v) {
                  handleBatchClaimSubmission(v ?? "");
                } else {
                  showSnackbar({
                    message: "Please select a claim vendor",
                    type: "error",
                  });
                }
              }}
            />
            <CopyClaimModal disclosureHost={copyClaimDisclosure} />
            {scrubClaimsDisclosureHost.opened && (
              <ScrubClaimsModal
                disclosureHost={scrubClaimsDisclosureHost}
                claimUuids={rowSelectionModel as string[]}
              />
            )}
            {/* {batchScrubClaimsDisclosureHost.opened && (
              <BatchClaimModal
                disclosureHost={batchScrubClaimsDisclosureHost}
              />
            )} */}
          </>
        )}

        {activeTab === "era_eob" && (
          <>
            <UploadEobModal disclosureHost={uploadEobDisclosure} />
            <UploadEraEobModal disclosureHost={uploadEraDisclosure} />
            {/* <EraEob
              filter={eraEobFilter}
              setFilter={setEraEobFilter}
              searchKey={eraEobSearchStringKey}
              uploadEobDisclosureHost={uploadEobDisclosure}
            /> */}
          </>
        )}

        {/* {activeTab === "ready_for_billing" && (
          <CustomDataGridV2
            getRowId={(row) => row?.uuid}
            rows={appointmentsList?.data?.content ?? []}
            columns={COLUMNS}
            rowCount={appointmentsList?.data?.page?.totalElements ?? 10}
            disableRowSelectionOnClick
            paginationModel={pagination}
            onPaginationModelChange={setPagination}
            onSortModelChange={setGridSorting}
            loading={isLoading || isFetching}
            rowHeight={BILLING_TABLE_ROW_HEIGHT}
          />
        )}  */}
        <Outlet
          context={{
            filter: selectedFilter[activeTab as keyof typeof selectedFilter],
            setFilter: setFilterFor(activeTab as keyof typeof selectedFilter),
            searchKey: instantSearchStringKey,
            onSearch: setSearchStringKey,
            rowSelectionModel,
            setRowSelectionModel,
            selectedClaimCard,
            setSelectedClaimCard,
            isBulkClaimActionAllowed,
            isReadyForSubmitView,
            paperClaimFilter,
          }}
        />
      </PageLayout>
    </ElevatedPaper>
  );
};

export default BillingLayout;
